<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state
      v-if="!selectedCompany"
      icon="mdi-domain"
      :title="$t('report_mobile.employees_pick_company')"
    ></report-state>
    <report-state v-else-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-account-cash-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      table-class="rd-tbl--workers"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="sums"
      :total-label="$t('total')"
      :selected="isOpen"
      @sort="sortBy"
      @open="openUser"
    >
      <template #cell-name="{ row }">
        <span class="rd-person">
          <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
          <span class="rd-person__name">{{ row.name }}</span>
        </span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import ReportCreditTabUsers from "@/components/report/more-restaurants/tabs/ReportCreditTabUsers.vue";
import { copyText, rowsToTsv } from "../../copyTable";
import hasMoreCreditRows from "../../../shared/more/hasMoreCreditRows";
import hasTableSort from "../../hasTableSort";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";
import ReportDesktopCreditUser from "./ReportDesktopCreditUser.vue";

/**
 * Kreditni korisnici (firme "više restorana") na računaru: koliko su korisnici naručili, potrošili na kredit i
 * koliko im je ostalo. Klik na red otvara narudžbe tog korisnika po mesecima u panelu. Firmu i period čita iz
 * zajedničke trake, pretragu (po imenu ili ID-u) takođe. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "ReportDesktopMoreCredit",
  extends: ReportCreditTabUsers,
  mixins: [hasTableSort, hasMoreCreditRows],
  components: { ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "id", label: "ID", cls: "rd-col-id", num: true, muted: true, sortable: true },
        { key: "name", label: this.$t("name"), name: true, sortable: true },
        { key: "ordersCount", label: this.$t("orders"), align: "end", num: true, zero: true, sortable: true },
        { key: "ordersTotal", label: `${this.$t("orders_total")}${this.currencySuffix}`, align: "end", num: true, zero: true, sortable: true },
        { key: "spent", label: `${this.$t("spent_on_credits")}${this.currencySuffix}`, align: "end", num: true, zero: true, sortable: true },
        { key: "balance", label: `${this.$t("credit_balance")}${this.currencySuffix}`, align: "end", num: true, sortable: true, cellClass: (row) => ({ "is-alert": row.balance < 0 }) },
        { key: "go", label: "", go: true },
      ];
    },
    rows() {
      return this.sortRows(this.matchedRows);
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("select_date_and_filters");
    },
    kpis() {
      return [
        { key: "users", label: this.$t("users"), value: this.rows.length, tone: "inverse" },
        { key: "spent", label: this.$t("spent_on_credits"), value: this.sums.spent },
        { key: "balance", label: this.$t("credit_balance"), value: this.sums.balance },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `credit-${row.id}`);
    },
    /** Narudžbe korisnika po mesecima u panelu (isti podaci kao stari dijalog: /credits/report). */
    openUser(row, event) {
      const company = this.selectedCompany;
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `credit-${row.id}`,
        root: true,
        wide: true,
        title: row.name,
        subtitle: [`ID ${row.id}`, this.toLatin((company && (company.name || company.company)) || "")].filter(Boolean).join(" · "),
        component: ReportDesktopCreditUser,
        props: { user: row.source, companyId: company && company.id },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("name"),
        this.$t("orders"),
        this.$t("orders_total"),
        this.$t("spent_on_credits"),
        this.$t("credit_balance"),
      ];
      const rows = this.rows.map((row) => [row.id, row.name, row.ordersCount, row.ordersTotal, row.spent, row.balance]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
